import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LOAN_REQUEST_OFFER_SOURCE } from 'cpool-constants';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

type Props = {
    source: LOAN_REQUEST_OFFER_SOURCE;
    id: string;
    leadId: string;
};

type SentAt = SchemaLoanRequestResolver_GetAcceptedOffers[number]['sentAt'];

export const useOfferSentAtMutation = ({ id, leadId, source }: Props) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const queryKeyGetOffers = queryKeys.getAcceptedOffers(leadId);

    return useMutation({
        mutationFn: async (sentAt: SentAt) => {
            return APICommunicator.sendCommand({
                aggregateId: leadId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.changeOfferSentAt,
                payload: { id, sentAt, source },
            });
        },
        onError: () => {
            toast.error(t('OFFER_MANAGEMENT.TOASTS.SENT_AT_CHANGED_FAILED'));
        },
        onMutate: async (sentAt: SentAt) => {
            queryClient.setQueryData(queryKeyGetOffers, (data: SchemaLoanRequestResolver_GetAcceptedOffers) => {
                const mutatedData = structuredClone(data);
                const oldOffer = mutatedData?.find((offer) => offer.id === id);
                if (oldOffer === undefined) {
                    return data;
                }
                oldOffer.sentAt = sentAt;
                return mutatedData;
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: queryKeyGetOffers });
        },
        onSuccess: ({ position }) => {
            setQueryPosition(JSON.stringify(queryKeyGetOffers), position);
        },
    });
};
